import type { ComponentType } from "react";

import type { EventService } from "@/shared/domain/events/EventService";
import type { FileService } from "@/shared/domain/files/FileService";
import type { TopicService } from "@/shared/domain/topics/TopicService";
import { Goto } from "@/shared/navigation";
import type { WorkspaceActionPayload } from "@/shared/services/AiAgentThreadPubSubBus/models";

import type { ApprovalMode } from "./approvalModes";

/**
 * Required prefix for every client-declared tool name.
 *
 * Distinguishes client tools from server-side tools at every layer (API,
 * SDK, UI, Bedrock toolConfig). The underscore form is used so the resulting
 * name still matches Bedrock's Converse `toolSpec.name` pattern.
 *
 * Mirrors `CLIENT_TOOL_NAME_PREFIX` in
 * `packages/roboto/src/roboto/ai/core/record.py`.
 */
export const CLIENT_TOOL_NAME_PREFIX = "client_";

/**
 * Pattern enforced on every client tool's `name` field — mirrors the
 * Pydantic validator on `ClientToolSpec.name` in the SDK so a registry
 * misconfiguration trips at registration time rather than at the API.
 */
export const CLIENT_TOOL_NAME_PATTERN = /^client_[a-z][a-z0-9_]*$/;

/**
 * Returns true iff `name` is in the client-tool namespace.
 *
 * Used by the thread UI to decide whether to render a "Client Tool" indicator
 * and by the dispatcher to filter mixed-turn tool_uses (see
 * {@link findPendingClientToolUses}).
 */
export function isClientToolName(name: string): boolean {
  return name.startsWith(CLIENT_TOOL_NAME_PREFIX);
}

/**
 * Strips the client-namespace prefix for display.
 *
 * Server tool names are returned unchanged so the helper is safe to call on
 * any `tool_name`.
 */
export function displayToolName(name: string): string {
  return isClientToolName(name)
    ? name.slice(CLIENT_TOOL_NAME_PREFIX.length)
    : name;
}

/**
 * Specification for a client-side tool that executes in the browser.
 */
export interface ClientToolSpec {
  /** Unique name identifying the tool. Must match {@link CLIENT_TOOL_NAME_PATTERN}. */
  name: string;
  /** Human-readable description of what the tool does. */
  description: string;
  /** JSON Schema describing the tool's input parameters. */
  input_schema: Record<string, unknown>;
}

/**
 * Dependencies that client tools may need from React context.
 *
 * Pure functions can't call hooks, so the hook collects these deps and passes
 * them into the tool executor.
 */
export interface ClientToolDeps {
  goto: Goto;
  orgId?: string;
  eventService?: EventService;
  topicService?: TopicService;
  fileService?: FileService;
  isOnVisualizerPage?: () => boolean;
  publishWorkspaceAction?: (payload: WorkspaceActionPayload) => boolean;
  onEventCreated?: () => void;
}

/**
 * Execution result returned by every client tool's `execute` function.
 */
export interface ClientToolExecutionResult {
  response: Record<string, unknown>;
  success: boolean;
}

/**
 * Props passed to a tool's custom confirmation content component.
 */
export interface ConfirmationContentProps {
  /** The parameters the LLM proposed for this tool invocation. */
  params: Record<string, unknown>;
}

/**
 * Complete client tool including spec, execution, approval configuration,
 * and optional UI for the confirmation modal.
 *
 * This is the registry-facing interface — tools register instances of this
 * type (or use {@link createClientTool} to build one from a typed config).
 */
export interface ClientTool {
  spec: ClientToolSpec;

  /** Default approval mode for this tool (used when the user has no stored preference). */
  defaultApprovalMode: ApprovalMode;

  /**
   * Which approval modes the user is allowed to select for this tool.
   *
   * For destructive tools like delete_dataset, this should be
   * `[ApprovalMode.REQUIRE_APPROVAL]` to prevent users from disabling the
   * confirmation prompt. The default approval mode MUST be included in this list.
   */
  allowableApprovalModes: ApprovalMode[];

  /** Human-readable label for the settings modal (e.g., "Navigate", "Create Events"). */
  displayName: string;

  /** Short description shown in the settings modal. */
  displayDescription: string;

  /** Execute the tool with the given params. Called after approval (if required). */
  execute: (
    params: Record<string, unknown>,
    deps?: ClientToolDeps,
  ) => Promise<ClientToolExecutionResult>;

  /**
   * Optional React component rendered inside the confirmation modal when
   * this tool requires approval. Receives the proposed tool parameters and
   * renders a read-only preview of what the tool will do.
   *
   * If not provided, a generic confirmation dialog is shown with the raw
   * parameters displayed as formatted JSON.
   */
  ConfirmationContent?: ComponentType<ConfirmationContentProps>;
}

// ---------------------------------------------------------------------------
// Result builders
// ---------------------------------------------------------------------------

/**
 * Build an error result for a client tool execution.
 *
 * `details` is merged into the response alongside `error` — use it for
 * structured, machine-readable context (e.g. per-item failure reasons) that
 * lets the LLM adjust its request and retry. The `error` key is reserved:
 * a `details.error` entry is ignored so it can never clobber the message.
 */
export function toolError(
  message: string,
  details: Record<string, unknown> = {},
): ClientToolExecutionResult {
  return { response: { ...details, error: message }, success: false };
}

/**
 * Build a success result for a client tool execution.
 */
export function toolSuccess(
  action: string,
  extra: Record<string, unknown> = {},
): ClientToolExecutionResult {
  return { response: { success: true, action, ...extra }, success: true };
}

// ---------------------------------------------------------------------------
// Typed tool factory
// ---------------------------------------------------------------------------

/**
 * Configuration accepted by {@link createClientTool}.
 *
 * @typeParam T - The validated input type for the tool.
 */
export interface ClientToolConfig<T> {
  /** Unique name identifying the tool. */
  name: string;
  /** Human-readable description of what the tool does. */
  description: string;
  /** JSON Schema describing the tool's input parameters. */
  input_schema: Record<string, unknown>;
  /**
   * Optional type-guard that validates raw input before `execute` is called.
   *
   * When provided, the wrapper calls `validateInput(params)` first. If it
   * returns `false`, the tool returns a generic validation-error result
   * without ever calling `execute`.
   *
   * When omitted, all input is forwarded to `execute` unconditionally
   * (cast to `T`).
   */
  validateInput?: (raw: unknown) => raw is T;
  /**
   * Tool implementation that receives the validated input.
   *
   * If `validateInput` is provided and passes, `input` is guaranteed to
   * satisfy `T`. If `validateInput` is omitted, `input` is a blind cast
   * from the raw params — the tool author is responsible for defensive
   * checks inside `execute`.
   */
  execute: (
    input: T,
    deps?: ClientToolDeps,
  ) => ClientToolExecutionResult | Promise<ClientToolExecutionResult>;
  /** Default approval mode for this tool (used when the user has no stored preference). */
  defaultApprovalMode: ApprovalMode;
  /**
   * Which approval modes the user is allowed to select for this tool.
   * The default approval mode MUST be included in this list.
   */
  allowableApprovalModes: ApprovalMode[];
  /** Human-readable label for the settings modal (e.g., "Navigate", "Create Events"). */
  displayName: string;
  /** Short description shown in the settings modal. */
  displayDescription: string;
  /** Optional React component for rich approval preview. */
  ConfirmationContent?: ComponentType<ConfirmationContentProps>;
}

/**
 * Factory that builds a {@link ClientTool} from a typed configuration.
 *
 * The returned tool's `execute` wraps the caller-supplied `execute`
 * with an optional `validateInput` guard. If validation fails, a generic
 * error result is returned without calling `execute`.
 *
 * @example
 * ```ts
 * interface SearchInput { query: string }
 *
 * const searchTool = createClientTool<SearchInput>({
 *   name: "search",
 *   description: "Search datasets",
 *   input_schema: { type: "object", properties: { query: { type: "string" } }, required: ["query"] },
 *   execute(input) {
 *     return { response: { results: [] }, success: true };
 *   },
 *   defaultApprovalMode: ApprovalMode.FOLLOW_AUTO,
 *   allowableApprovalModes: [ApprovalMode.REQUIRE_APPROVAL, ApprovalMode.FOLLOW_AUTO, ApprovalMode.AUTO_EXECUTE],
 *   displayName: "Search",
 *   displayDescription: "Searches datasets.",
 * });
 * ```
 */
export function createClientTool<T>(config: ClientToolConfig<T>): ClientTool {
  if (!CLIENT_TOOL_NAME_PATTERN.test(config.name)) {
    // Fail fast at registration so a misnamed tool can never reach the API,
    // where the Pydantic validator on ClientToolSpec.name would reject the
    // whole request and break the thread session.
    throw new Error(
      `Client tool name "${config.name}" must match ${CLIENT_TOOL_NAME_PATTERN}` +
        ` (start with "${CLIENT_TOOL_NAME_PREFIX}", then lowercase letters,` +
        ` digits, and underscores).`,
    );
  }
  return {
    spec: {
      name: config.name,
      description: config.description,
      input_schema: config.input_schema,
    },
    defaultApprovalMode: config.defaultApprovalMode,
    allowableApprovalModes: config.allowableApprovalModes,
    displayName: config.displayName,
    displayDescription: config.displayDescription,
    ConfirmationContent: config.ConfirmationContent,
    async execute(params, deps) {
      if (config.validateInput && !config.validateInput(params)) {
        return { response: { error: "Invalid input" }, success: false };
      }
      return config.execute(params as T, deps);
    },
  };
}
